import { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { Button } from "ui/button";
import { TextField } from "ui/text-field";
import { Switch } from "ui/switch";

import {
  IEmailTypesDetails,
  ISenderDetails,
  IEmailTypesSendersForm,
} from "containers/admin/clients/client/components/email-types/components/email-types-senders-form/email-types-senders-form.types";

import { EMAIL_TYPES_DETAILS_VALIDATION_SCHEMA } from "containers/admin/clients/client/components/email-types/components/email-types-senders-form/email-types-senders-form.constants";
import { NEW_SENDER_DATA } from "containers/admin/clients/client/components/email-types/email-types.constants";

import Icons from "assets";

const EmailTypesSendersForm = ({ onCancel, saveSenders, email }: IEmailTypesSendersForm) => {
  const [isSubmitBtnDisabled, setIsSubmitBtnDisabled] = useState(true);

  //form
  const {
    handleSubmit,
    control,
    formState: { dirtyFields, errors },
    watch,
    getValues,
    setValue,
  } = useForm<IEmailTypesDetails>({
    mode: "onChange",
    defaultValues: {
      senders: email.senders,
    },
    resolver: EMAIL_TYPES_DETAILS_VALIDATION_SCHEMA,
  });

  const { fields, append, remove } = useFieldArray({
    name: "senders",
    control,
    keyName: "_tempHookFormId",
  });

  const watchedAllSendersFilled = watch("senders").every((senderData: ISenderDetails) => {
    const { senderName, replyToEmail, senderEmail } = senderData;

    return (
      Object.keys({ senderName, replyToEmail, senderEmail }) as Array<keyof ISenderDetails>
    ).every((key) => !!senderData[key]);
  });

  const resetSenders = (newDefaultSender: string) => {
    for (let i = 0; i < fields.length; i++) {
      fields[i]._tempHookFormId !== newDefaultSender &&
        setValue(`senders.${i}.defaultForEmailType`, false);
    }
  };

  const formatSenderData = (formValues: IEmailTypesDetails) => {
    const formattedSendersData = fields.map((field, ind) => {
      const updatedSender = {
        ...formValues.senders[ind],
        //react-hook-form has issues with boolean values and it can not register them properly so that's why we set it manually below
        defaultForEmailType: getValues(`senders.${ind}.defaultForEmailType`),
      };

      const newSender = field.id
        ? {
            ...updatedSender,
            //set id if sender existed
            id: field?.id,
          }
        : //if sender is new - set it without id
          updatedSender;

      return newSender;
    });

    saveSenders(formattedSendersData);
  };

  useEffect(() => {
    const noErrorsOccured = !errors.senders?.length;

    const atLeastOneFieldTouched = !!dirtyFields?.senders?.length;

    //check if all 3 fields in sender data filled, if there are no errs and if at least one field is touched
    const isDisabled = !(watchedAllSendersFilled && noErrorsOccured && atLeastOneFieldTouched);

    setIsSubmitBtnDisabled(isDisabled);
  }, [watchedAllSendersFilled, dirtyFields?.senders?.length, errors.senders?.length]);

  return (
    <form
      onSubmit={handleSubmit(formatSenderData)}
      data-testid="email-types-details-table"
      className="flex flex-col gap-2"
    >
      {fields.map((formField, index) => {
        const watchDefaultSender = watch(`senders.${index}.defaultForEmailType`);

        return (
          <section
            className={twMerge(
              "flex flex-col border border-gray-1 rounded-md p-5 gap-2",
              fields.length > 1 && "pb-2",
            )}
            key={formField._tempHookFormId}
          >
            <div className="flex justify-between">
              <span className="font-semibold text-navy-800">Sender {index + 1}</span>
              <Controller
                name={`senders.${index}.defaultForEmailType`}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Switch
                    toggleName="Default sender"
                    labelPlacement="end"
                    sx={value ? { opacity: 0.5 } : undefined}
                    labelSx={(theme) => ({ color: theme.palette.grey[500] })}
                    checked={value}
                    onChange={(value) => {
                      if (value) {
                        resetSenders(formField._tempHookFormId);
                        onChange(value);
                      }
                    }}
                  />
                )}
              />
            </div>
            <Controller
              name={`senders.${index}.senderName`}
              control={control}
              render={({ field }) => (
                <TextField
                  required
                  errMessage={errors?.senders?.[index]?.senderName?.message}
                  data-testid={"sender-name-input" + index}
                  label="Sender name"
                  {...field}
                  value={field.value || ""}
                />
              )}
            />
            <Controller
              name={`senders.${index}.senderEmail`}
              control={control}
              render={({ field }) => (
                <TextField
                  required
                  errMessage={errors?.senders?.[index]?.senderEmail?.message}
                  data-testid="sender-email-input"
                  label="Sender email"
                  {...field}
                  value={field.value || ""}
                />
              )}
            />
            <Controller
              name={`senders.${index}.replyToEmail`}
              control={control}
              render={({ field }) => (
                <TextField
                  required
                  errMessage={errors?.senders?.[index]?.replyToEmail?.message}
                  data-testid="reply-to-email-input"
                  label="Reply-to email"
                  {...field}
                  value={field.value || ""}
                />
              )}
            />
            {fields.length > 1 && (
              <Button
                size="small"
                color="inherit"
                variant="text"
                startIcon={<Icons.Trash />}
                disabled={watchDefaultSender}
                onClick={() => !watchDefaultSender && remove(index)}
              >
                Delete
              </Button>
            )}
          </section>
        );
      })}
      <div className="flex flex-col gap-4">
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setIsSubmitBtnDisabled(true);
            append({
              ...NEW_SENDER_DATA,
              defaultForEmailType: false,
            });
          }}
        >
          Add sender details
        </Button>
        <div className="flex gap-4">
          <Button size="small" variant="contained" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="small" type="submit" disabled={isSubmitBtnDisabled}>
            Save changes
          </Button>
        </div>
      </div>
    </form>
  );
};

export default EmailTypesSendersForm;
